'use client';

// Device-scoped "first proposal created" flag (spec 113-proposal-flow-redesign,
// FR-016/FR-018). The first time a creator successfully creates a market we show
// a one-time "what happens next" onboarding panel before the share screen; on
// every subsequent creation we go straight to the share screen. Detection is a
// device-local localStorage flag (clarification 2026-06-10) — a brand-new device
// may re-show the panel once, which is acceptable.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { createTrackedStore } from '@/lib/dev/zustand-registry';

// FR-018: persistence failure must NOT surface to the user or block reaching the
// share screen / completing creation. The default `localStorage` adapter throws
// on quota / private-mode failures; wrap every call so in-memory state still
// works (the panel may then re-show — acceptable per spec).
const SAFE_STORAGE: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      return globalThis.localStorage?.getItem(name) ?? null;
    } catch {
      // SILENT-CATCH: spec 113 FR-018 — storage unavailable must NOT surface; degrade to in-memory.
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      globalThis.localStorage?.setItem(name, value);
    } catch {
      // SILENT-CATCH: spec 113 FR-018 — storage write failure (quota / private mode) must degrade silently.
    }
  },
  removeItem: (name: string): void => {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      // SILENT-CATCH: spec 113 FR-018 — symmetric with setItem; remove failure must not surface.
    }
  },
};

interface ProposalFirstCreationState {
  /** True once the one-time onboarding panel has been shown. */
  seen: boolean;
  /** Idempotently record that the onboarding panel has been shown. */
  markSeen: () => void;
}

export const useProposalFirstCreationStore: UseBoundStore<StoreApi<ProposalFirstCreationState>> =
  createTrackedStore<ProposalFirstCreationState>('useProposalFirstCreationStore')(
    persist(
      (set, get) => ({
        seen: false,
        markSeen: (): void => {
          if (get().seen) return;
          set({ seen: true });
        },
      }),
      {
        name: 'morfi-proposal-first-creation-seen',
        partialize: (
          state: ProposalFirstCreationState,
        ): Pick<ProposalFirstCreationState, 'seen'> => ({ seen: state.seen }),
        storage: createJSONStorage(() => SAFE_STORAGE),
      },
    ),
  );
